import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const JITSI_DOMAIN = 'meet.jit.si';
const JITSI_SCRIPT_URL = `https://${JITSI_DOMAIN}/external_api.js`;

function VideoLlamada() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [citaInfo, setCitaInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(false);
  const [nombre, setNombre] = useState('');
  const jitsiContainerRef = useRef(null);
  const apiRef = useRef(null);
  const displayNameRef = useRef('');

  useEffect(() => {
    if (!token) { setError('Token no proporcionado'); setLoading(false); return; }
    fetch(`${API_BASE}/videollamada.php?token=${encodeURIComponent(token)}`)
      .then(r => { if (!r.ok) throw new Error(r.status === 404 ? 'Sala no encontrada' : 'Error al cargar'); return r.json(); })
      .then(data => { if (data.error) throw new Error(data.error); setCitaInfo(data); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleLeave = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.dispose();
      apiRef.current = null;
    }
    setJoined(false);
  }, []);

  const startCall = useCallback(() => {
    if (!citaInfo) return;
    displayNameRef.current = nombre.trim() || 'Participante';
    setJoined(true);
  }, [citaInfo, nombre]);

  useEffect(() => {
    if (!joined || !citaInfo || !jitsiContainerRef.current || apiRef.current) return;

    const roomName = `crm-${citaInfo.sala_video_token}`;
    const displayName = displayNameRef.current;

    const initJitsi = () => {
      if (!window.JitsiMeetExternalAPI || !jitsiContainerRef.current) return;
      const api = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, {
        roomName,
        parentNode: jitsiContainerRef.current,
        userInfo: { displayName },
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          prejoinPageEnabled: false,
          disableDeepLinking: true,
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          DEFAULT_BACKGROUND: '#1a1a2e',
          TOOLBAR_BUTTONS: [
            'microphone', 'camera', 'desktop', 'chat',
            'raisehand', 'tileview', 'hangup',
          ],
        },
      });
      api.addListener('readyToClose', handleLeave);
      apiRef.current = api;
    };

    const existing = document.querySelector(`script[src="${JITSI_SCRIPT_URL}"]`);
    if (existing && window.JitsiMeetExternalAPI) {
      initJitsi();
    } else if (!existing) {
      const script = document.createElement('script');
      script.src = JITSI_SCRIPT_URL;
      script.async = true;
      script.onload = initJitsi;
      document.head.appendChild(script);
    } else {
      existing.addEventListener('load', initJitsi, { once: true });
    }
  }, [joined, citaInfo, handleLeave]);

  useEffect(() => {
    return () => {
      if (apiRef.current) {
        apiRef.current.dispose();
        apiRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
        <p style={{ marginTop: 16, color: '#666' }}>Cargando sala...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.center}>
        <div style={styles.card}>
          <h2 style={{ color: '#dc3545', marginBottom: 12 }}>Error</h2>
          <p>{error}</p>
          <button style={styles.btnSecondary} onClick={() => navigate('/')}>Volver</button>
        </div>
      </div>
    );
  }

  if (!joined) {
    const fechaHora = citaInfo?.fecha_hora
      ? new Date(citaInfo.fecha_hora).toLocaleString('es-ES', {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })
      : '';

    return (
      <div style={styles.center}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>📹</div>
            <h2 style={{ margin: 0, color: '#2563eb' }}>Videollamada</h2>
          </div>

          <div style={styles.infoBox}>
            <p style={styles.infoRow}><strong>Sesión:</strong> {citaInfo?.titulo || 'Sesión'}</p>
            <p style={styles.infoRow}><strong>Fecha:</strong> {fechaHora}</p>
            <p style={styles.infoRow}><strong>Duración:</strong> {citaInfo?.duracion_minutos || 60} min</p>
            {citaInfo?.coach && <p style={styles.infoRow}><strong>Coach:</strong> {citaInfo.coach}</p>}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>
              Tu nombre
            </label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Escribe tu nombre..."
              style={styles.input}
              onKeyDown={(e) => e.key === 'Enter' && nombre.trim() && startCall()}
            />
          </div>

          <button
            style={{ ...styles.btnPrimary, opacity: !nombre.trim() ? 0.5 : 1 }}
            disabled={!nombre.trim()}
            onClick={startCall}
          >
            Unirse a la videollamada
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.fullScreen}>
      <div style={styles.topBar}>
        <span style={{ fontWeight: 600, color: '#fff' }}>
          {citaInfo?.titulo || 'Videollamada'}
        </span>
        <button style={styles.btnLeave} onClick={handleLeave}>
          Salir
        </button>
      </div>
      <div ref={jitsiContainerRef} style={styles.jitsiContainer} />
    </div>
  );
}

const styles = {
  center: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '100vh', background: '#f0f2f5', padding: 20,
  },
  card: {
    background: '#fff', borderRadius: 12, padding: '32px 28px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)', maxWidth: 440, width: '100%',
  },
  infoBox: {
    background: '#f8f9fa', borderRadius: 8, padding: '14px 16px',
    marginBottom: 20, borderLeft: '4px solid #2563eb',
  },
  infoRow: { margin: '4px 0', fontSize: 14, color: '#333' },
  input: {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box',
  },
  btnPrimary: {
    width: '100%', padding: '12px 0', background: '#2563eb', color: '#fff',
    border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: 'pointer',
  },
  btnSecondary: {
    marginTop: 16, padding: '10px 24px', background: '#e9ecef', color: '#333',
    border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer',
  },
  btnLeave: {
    padding: '6px 18px', background: '#dc3545', color: '#fff',
    border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  fullScreen: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    display: 'flex', flexDirection: 'column', background: '#1a1a2e', zIndex: 9999,
  },
  topBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 16px', background: '#0d0d1a', zIndex: 1,
  },
  jitsiContainer: { flex: 1, width: '100%' },
  spinner: {
    width: 40, height: 40, border: '4px solid #e9ecef',
    borderTopColor: '#2563eb', borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

export default VideoLlamada;
